import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  getMyOrganization,
  listOrganizationUsers,
  inviteUser,
  editUser,
  toggleUserStatus,
  deleteUser,
  listMyRestaurants,
  setUserPin,
} from '../services/api';
import { useUserRole } from '../hooks/useUserRole';
import AvatarUpload from '../components/AvatarUpload';
import HelpIcon from '../components/help/HelpIcon';
import { roleUsesPin, sanitizePinInput, validatePinPair } from '../utils/comandaUsers';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import './UserFormPage.css';

function UserFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { isOwner } = useUserRole();
  const isEditMode = !!userId;
  const { setDynamicLabel } = useBreadcrumb();

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Organization and current user data
  const [organization, setOrganization] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'ADMIN',
    restaurantIds: [],
    avatarUrl: '',
  });

  // PIN de comanda (RES-113): no forma parte de editUser, tiene su propio endpoint
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [resettingPin, setResettingPin] = useState(false);

  // Restaurants state
  const [restaurants, setRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);

  // Toggle status modal
  const [showToggleStatusConfirm, setShowToggleStatusConfirm] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

  // Delete user modal (permanent removal from org)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadOrganization();
  }, []);

  useEffect(() => {
    if (isEditMode && formData.firstName) {
      setDynamicLabel(formData.firstName + ' ' + formData.lastName);
    }
  }, [isEditMode, formData.firstName, formData.lastName, setDynamicLabel]);

  useEffect(() => {
    if (organization?.organizationId) {
      loadRestaurants();
      if (isEditMode) {
        loadUser();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, userId]);

  const loadOrganization = async () => {
    try {
      const orgResponse = await getMyOrganization();
      const firstOrg = orgResponse.organizations?.[0];
      if (firstOrg) {
        setOrganization(firstOrg);
      } else {
        setError('No se encontró ninguna organización');
      }
    } catch (err) {
      console.error('Error loading organization:', err);
      setError('Error al cargar organización');
    }
  };

  const loadRestaurants = async () => {
    try {
      setLoadingRestaurants(true);
      const response = await listMyRestaurants(organization.organizationId);
      setRestaurants(response.restaurants || []);
    } catch (err) {
      console.error('Error loading restaurants:', err);
      setRestaurants([]);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const loadUser = async () => {
    try {
      setLoading(true);
      const response = await listOrganizationUsers(organization.organizationId);
      const user = response.users?.find((u) => u.userId === userId);

      if (user) {
        setCurrentUser(user);
        setFormData({
          email: user.email || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phone: user.phone || '',
          role: (user.role || 'ADMIN').toUpperCase(),
          restaurantIds: user.restaurants || [],
          avatarUrl: user.avatarUrl || '',
        });
      } else {
        setError('Usuario no encontrado');
      }
    } catch (err) {
      console.error('Error loading user:', err);
      setError('Error al cargar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!organization?.organizationId) {
      setError('No se encontró la organización');
      return;
    }

    // ADMIN, COMANDA, BARRA y STAFF necesitan un local: sin él hasRestaurantAccess los rechaza
    if (roleUsesPin(formData.role) && formData.restaurantIds.length === 0) {
      setError('Debes seleccionar al menos un restaurante para este rol');
      return;
    }

    // El PIN es opcional al invitar; si se carga, tiene que ser válido antes de enviar nada
    if (!isEditMode && roleUsesPin(formData.role) && (pin || confirmPin)) {
      const pinError = validatePinPair(pin, confirmPin);
      if (pinError) {
        setError(t(`users.pin.${pinError}`));
        return;
      }
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const dataToSend = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        role: formData.role,
        restaurantIds: formData.role === 'OWNER' ? [] : formData.restaurantIds,
        avatarUrl: formData.avatarUrl,
      };

      if (isEditMode) {
        await editUser(organization.organizationId, userId, dataToSend);
        setMessage('Usuario actualizado exitosamente');
      } else {
        const invited = await inviteUser(organization.organizationId, {
          email: formData.email,
          ...dataToSend,
        });
        setMessage('Invitación enviada exitosamente');

        // El usuario recién existe tras la invitación: recién ahí se le puede cargar el PIN
        if (roleUsesPin(formData.role) && pin && invited?.user?.userId) {
          try {
            await setUserPin(organization.organizationId, invited.user.userId, pin);
          } catch (pinErr) {
            console.error('Error setting PIN after invite:', pinErr);
            setError(t('users.pin.setAfterInviteError'));
            setTimeout(() => navigate('/users'), 3000);
            return;
          }
        }
      }

      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.message || 'Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPin = async () => {
    if (!organization?.organizationId || !currentUser?.userId) return;

    const pinError = validatePinPair(pin, confirmPin);
    if (pinError) {
      setMessage('');
      setError(t(`users.pin.${pinError}`));
      return;
    }

    setResettingPin(true);
    setError('');
    setMessage('');
    try {
      await setUserPin(organization.organizationId, currentUser.userId, pin);
      setCurrentUser((prev) => ({ ...prev, hasPin: true }));
      setPin('');
      setConfirmPin('');
      setMessage(t('users.pin.resetSuccess'));
    } catch (err) {
      console.error('Error resetting PIN:', err);
      setError(err.response?.data?.message || t('users.pin.resetError'));
    } finally {
      setResettingPin(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!organization?.organizationId || !currentUser?.userId) {
      return;
    }

    setTogglingStatus(true);
    try {
      await toggleUserStatus(organization.organizationId, currentUser.userId, currentUser.status);
      const action = currentUser.status === 'active' ? 'desactivado' : 'activado';
      setMessage(`Usuario ${action} exitosamente`);

      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError('Error al cambiar el estado del usuario');
    } finally {
      setTogglingStatus(false);
      setShowToggleStatusConfirm(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!organization?.organizationId || !currentUser?.userId) {
      return;
    }

    setDeleting(true);
    try {
      await deleteUser(organization.organizationId, currentUser.userId);
      setMessage('Usuario eliminado de la organización exitosamente');

      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Error al eliminar usuario');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleRestaurantToggle = (restaurantId) => {
    setFormData((prev) => ({
      ...prev,
      restaurantIds: prev.restaurantIds.includes(restaurantId)
        ? prev.restaurantIds.filter((id) => id !== restaurantId)
        : [...prev.restaurantIds, restaurantId],
    }));
  };

  const handleAvatarUploaded = (avatarUrl) => {
    setFormData((prev) => ({ ...prev, avatarUrl }));
  };

  const handleCancel = () => {
    navigate('/users');
  };

  // Locales del usuario que ya exigen identificación por mozo: sin PIN no podría entrar a la comanda
  const identificationRestaurants = restaurants.filter(
    (r) =>
      formData.restaurantIds.includes(r.restaurantId) &&
      r.settings?.comandaSecurity?.staffIdentification === true
  );
  const showMissingPinWarning =
    roleUsesPin(formData.role) &&
    identificationRestaurants.length > 0 &&
    !pin &&
    !(isEditMode && currentUser?.hasPin);

  // Check if user has permission
  if (!isOwner()) {
    return (
      <div className="user-form-page">
        <div className="alert alert-warning">Solo los propietarios pueden gestionar usuarios.</div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Cargando usuario...</div>;
  }

  return (
    <div className="user-form-page">
      <div className="form-header">
        <div>
          <h1>{isEditMode ? 'Editar Usuario' : 'Invitar Usuario'}</h1>
          <p>
            {isEditMode
              ? 'Actualiza la información del usuario'
              : 'Completa la información para invitar a un nuevo usuario'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="user-form">
        {/* Sección: Información Básica */}
        <div className="form-section">
          <h2>Información Básica</h2>

          {/* Avatar Upload (solo en modo edición) */}
          {isEditMode && organization?.organizationId && userId && (
            <div className="form-group avatar-section">
              <AvatarUpload
                organizationId={organization.organizationId}
                userId={userId}
                currentAvatarUrl={formData.avatarUrl}
                onAvatarUploaded={handleAvatarUploaded}
              />
            </div>
          )}

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={isEditMode}
              placeholder="usuario@ejemplo.com"
              style={isEditMode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            />
            {isEditMode && <small className="form-hint">El email no puede ser modificado</small>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Nombre *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                placeholder="Juan"
              />
            </div>

            <div className="form-group">
              <label>Apellido *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                placeholder="Pérez"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Teléfono</label>
            <PhoneInput
              country={'ar'}
              value={formData.phone}
              onChange={(phone) => setFormData({ ...formData, phone })}
              inputProps={{
                required: false,
              }}
              containerClass="phone-input-container"
              inputClass="phone-input"
              buttonClass="phone-input-button"
              preferredCountries={['ar', 'uy', 'cl', 'br']}
            />
            <small className="form-hint">Opcional</small>
          </div>
        </div>

        {/* Sección: Permisos y Acceso */}
        <div className="form-section">
          <h2>Permisos y Acceso</h2>

          <div className="form-group">
            <label>Rol *</label>
            <select
              value={formData.role}
              onChange={(e) => {
                const newRole = e.target.value;
                setFormData({
                  ...formData,
                  role: newRole,
                  restaurantIds: newRole === 'OWNER' ? [] : formData.restaurantIds,
                });
              }}
              required
            >
              <option value="ADMIN">{t('users.admin')}</option>
              <option value="COMANDA">{t('users.comanda')}</option>
              <option value="BARRA">{t('users.barra')}</option>
              <option value="STAFF">{t('users.staff')}</option>
              <option value="OWNER">{t('users.owner')}</option>
            </select>
            <small className="form-hint">
              {formData.role === 'OWNER' && `⚠️ ${t('users.ownerHint')}`}
              {formData.role === 'ADMIN' && t('users.adminHint')}
              {formData.role === 'COMANDA' && t('users.comandaHint')}
              {formData.role === 'BARRA' && t('users.barraHint')}
              {formData.role === 'STAFF' && t('users.staffHint')}
            </small>
          </div>

          {/* Selector de restaurantes para ADMIN, COMANDA, BARRA y STAFF */}
          {roleUsesPin(formData.role) && (
            <div className="form-group">
              <label>Restaurantes Asignados *</label>
              <p className="form-description">
                Selecciona los restaurantes a los que este usuario tendrá acceso.
              </p>

              {loadingRestaurants ? (
                <p>Cargando restaurantes...</p>
              ) : restaurants.length === 0 ? (
                <p className="empty-message">No hay restaurantes disponibles. Crea uno primero.</p>
              ) : (
                <div className="restaurants-grid">
                  {restaurants.map((restaurant) => (
                    <label key={restaurant.restaurantId} className="restaurant-checkbox-card">
                      <input
                        type="checkbox"
                        checked={formData.restaurantIds.includes(restaurant.restaurantId)}
                        onChange={() => handleRestaurantToggle(restaurant.restaurantId)}
                      />
                      <div className="restaurant-info">
                        <div className="restaurant-name">{restaurant.name}</div>
                        {restaurant.address && (
                          <div className="restaurant-address">{restaurant.address}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {formData.role === 'OWNER' && (
            <div className="owner-notice">
              <strong>Acceso Automático</strong>
              <p>
                Los usuarios con rol OWNER tienen acceso automático a todos los restaurantes de la
                organización.
              </p>
            </div>
          )}
        </div>

        {/* Sección: PIN de comanda (RES-113) */}
        {roleUsesPin(formData.role) && (
          <div className="form-section" data-testid="pin-section">
            <h2>
              {t('users.pin.title')} <HelpIcon topicKey="users.pin" />
            </h2>
            <p className="form-description">{t('users.pin.description')}</p>

            {isEditMode && currentUser && (
              <p className="form-hint" data-testid="pin-status">
                {currentUser.hasPin ? t('users.pin.configured') : t('users.pin.missing')}
              </p>
            )}

            {showMissingPinWarning && (
              <div className="alert alert-warning" data-testid="pin-missing-warning">
                {t('users.pin.missingWarning')}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="comanda-pin">
                  {isEditMode ? t('users.pin.newPin') : t('users.pin.pin')}
                </label>
                <input
                  id="comanda-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(sanitizePinInput(e.target.value))}
                  placeholder="••••"
                />
              </div>
              <div className="form-group">
                <label htmlFor="comanda-pin-confirm">{t('users.pin.confirm')}</label>
                <input
                  id="comanda-pin-confirm"
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  maxLength={6}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(sanitizePinInput(e.target.value))}
                  placeholder="••••"
                />
              </div>
            </div>
            <small className="form-hint">{t('users.pin.hint')}</small>

            {isEditMode && (
              <div className="form-actions-left">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleResetPin}
                  disabled={saving || resettingPin || !pin || !confirmPin}
                >
                  {resettingPin ? t('users.pin.resetting') : t('users.pin.reset')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Mensajes */}
        {error && <div className="message error">{error}</div>}

        {message && <div className="message success">{message}</div>}

        {/* Acciones del formulario */}
        <div className="form-actions">
          <div className="form-actions-left">
            {isEditMode && currentUser && (
              <>
                <button
                  type="button"
                  className={`btn ${currentUser.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => setShowToggleStatusConfirm(true)}
                  disabled={saving || togglingStatus || deleting}
                >
                  {currentUser.status === 'active' ? 'Desactivar Usuario' : 'Activar Usuario'}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={saving || togglingStatus || deleting}
                >
                  Eliminar de Organización
                </button>
              </>
            )}
          </div>
          <div className="form-actions-right">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleCancel}
              disabled={saving || togglingStatus || deleting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || togglingStatus || deleting}
            >
              {saving ? 'Guardando...' : isEditMode ? 'Guardar Cambios' : 'Enviar Invitación'}
            </button>
          </div>
        </div>
      </form>

      {/* Modal de confirmación de toggle status */}
      {showToggleStatusConfirm && currentUser && (
        <div className="modal-overlay" onClick={() => setShowToggleStatusConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{currentUser.status === 'active' ? 'Desactivar Usuario' : 'Activar Usuario'}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowToggleStatusConfirm(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>
                {currentUser.status === 'active'
                  ? `¿Estás seguro que deseas desactivar a ${formData.firstName} ${formData.lastName}?`
                  : `¿Estás seguro que deseas activar a ${formData.firstName} ${formData.lastName}?`}
              </p>
              <p className="warning-text">
                {currentUser.status === 'active'
                  ? 'El usuario no podrá acceder al sistema hasta que sea reactivado.'
                  : 'El usuario podrá acceder al sistema nuevamente.'}
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowToggleStatusConfirm(false)}
                disabled={togglingStatus}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn ${currentUser.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                onClick={handleToggleStatus}
                disabled={togglingStatus}
              >
                {togglingStatus
                  ? 'Procesando...'
                  : currentUser.status === 'active'
                    ? 'Sí, Desactivar'
                    : 'Sí, Activar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación permanente */}
      {showDeleteConfirm && currentUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Eliminar Usuario de la Organización</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>
                ¿Estás seguro que deseas eliminar a{' '}
                <strong>
                  {formData.firstName} {formData.lastName}
                </strong>{' '}
                de la organización?
              </p>
              <p className="danger-text">
                Esta acción desvinculará completamente al usuario de la organización y todos sus
                restaurantes. El usuario deberá ser invitado nuevamente si desea volver a tener
                acceso.
              </p>
              <p className="info-text">
                Nota: La cuenta de Cognito del usuario NO será eliminada, solo su vinculación con
                esta organización.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteUser}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Sí, Eliminar de Organización'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserFormPage;
